import clsx from 'clsx'
import Joi from 'joi'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import SelectField from '../../../../../../UI/SelectField'

import SettingHint from '../SettingHint'
import TemplateLinks from '../TemplateLinks'

import { TLanguages } from '../../../../../../shared/const'

import styles from '../style.module.scss'

/*
 * Lebensmittelhygiene und Allergeninformation (Anforderung #96).
 *
 * Hier steht, **wann** die Bestätigung verlangt wird (welche Branchen).
 * Ob der Punkt überhaupt in der Checkliste erscheint, entscheidet der Schalter
 * „Hygiene- und Allergenschulung" in der Karte darüber — beides zusammen ergibt
 * die Regel, deshalb stehen sie in derselben Gruppe.
 *
 * Die Werte müssen zu services/companies/hygieneTraining.js im Backend passen.
 *
 * Abstand und Versandweg der Erinnerung sind mit Anforderung #183 entfallen:
 * Zu „im Lehrplan integriert" gibt es keine Zusage mehr, an die erinnert werden
 * könnte. Früher gespeicherte Werte bleiben in der Einstellung liegen und
 * werden beim Speichern unverändert mitgegeben — gelesen werden sie nicht mehr.
 */

const DEFAULTS = {
	branches: ['Health & Beauty', 'Food & Drinks']
}

const schema = Joi.object({
	hygieneTraining: Joi.object().keys({
		branches: Joi.array().items(Joi.string()).allow(null).default([])
	})
})

function HygieneTrainingSettings(props) {
	const { t, i18n } = useTranslation()
	const {
		reset,
		control,
		handleSubmit,
		formState: { errors }
	} = useForm({ resolver: joiResolver(schema) })

	const branchOptions = (props.branch || []).map(entry => ({
		value: entry.value_EN,
		label: i18n.language === TLanguages.EN ? entry.value_EN : entry.value_DE
	}))

	const onSubmit = form => {
		const value = form.hygieneTraining

		props
			.updateOptions({
				name: 'hygieneTraining',
				value: {
					...(props.form?.hygieneTraining || {}),
					branches: value.branches || []
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		const value = props.form?.hygieneTraining || {}
		reset({ hygieneTraining: { branches: Array.isArray(value.branches) ? value.branches : DEFAULTS.branches } })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="hygieneTraining" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Hygiene- und Allergenschulung</h3>
				<SettingHint
					example="Beispiel: Gesundheit &amp; Schönheit, Nahrungsmittel &amp; Getränke."
					warning="Ohne Branche in der Liste erscheint der Punkt bei keiner Junior Company."
				>
					Junior Companies dieser Branchen müssen im Unternehmensprofil bestätigen, wie Lebensmittelhygiene
					und Allergeninformation bei ihnen abgedeckt sind — im Lehrplan integriert oder Schulung bereits
					absolviert. Ist die Schulung nicht im Lehrplan abgedeckt, muss sie vor Beginn der Herstellung
					absolviert werden. Ohne diese Bestätigung lässt sich die Junior Company nicht zur Aktivierung
					einreichen. Die betreuende Lehrkraft wird über jede Bestätigung informiert und kann widersprechen.
				</SettingHint>
				<TemplateLinks
					items={[
						{ channel: 'internal', key: 'HYGIENE_CONFIRMED' },
						{ channel: 'internal', key: 'HYGIENE_OBJECTION' }
					]}
				/>
				<div className={styles.sectionContent}>
					<div className={clsx(styles.flexContainer, styles.fieldsEven)}>
						<SelectField
							isMulti
							valuesToOptions
							className={styles.field}
							label="Branchen, für die die Bestätigung gilt"
							placeholder={t('Please select')}
							options={branchOptions}
							control={control}
							name="hygieneTraining.branches"
							error={errors.hygieneTraining?.branches}
						/>
					</div>
					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

HygieneTrainingSettings.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired,
	branch: PropTypes.array
}

const mapStateToProps = state => ({
	branch: state.options.branch
})

export default connect(mapStateToProps)(HygieneTrainingSettings)
